import React, { useState } from 'react';
import { Tabs, Card, Table, Button, Form, Input, Select, Space, Typography, Popconfirm, message, Tag, Badge, Progress } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { useTableRequest } from '@/hooks';
import dayjs from 'dayjs';
import {
  Firmware,
  OtaJob,
  getFirmwares,
  deleteFirmware,
  getOtaJobs,
  startOtaJob,
  cancelOtaJob,
  retryOtaJob,
} from '@/api/ota';
import FirmwareModal from './FirmwareModal';
import CreateJobDrawer from './CreateJobDrawer';
import JobDetailDrawer from './JobDetailDrawer';

const { Text } = Typography;

const jobStatusMap: Record<string, { color: string; label: string }> = {
  pending: { color: 'default', label: '待升级' },
  offered: { color: 'blue', label: '已下发' },
  transferring: { color: 'cyan', label: '传输中' },
  verifying: { color: 'gold', label: '校验中' },
  success: { color: 'green', label: '成功' },
  failed: { color: 'red', label: '失败' },
  rejected: { color: 'volcano', label: '已拒绝' },
  canceled: { color: 'gray', label: '已取消' },
};

function formatSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export const FirmwareTab: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { loading, dataSource, pagination, form, reload, search, reset } = useTableRequest<Firmware>({
    onRequest: (params) => getFirmwares(params),
  });

  const handleDelete = async (id: string) => {
    const resp = await deleteFirmware(id);
    if (resp.success) {
      messageApi.success('删除成功');
      reload();
    } else {
      messageApi.error(resp.errorMsg || '删除失败');
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="inline" className="ml20 mt20" form={form}>
        <Form.Item name="name">
          <Input placeholder="固件名称" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="version">
          <Input placeholder="版本号" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={search}>查询</Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={reset}>重置</Button>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={() => setUploadOpen(true)}>上传固件</Button>
        </Form.Item>
      </Form>
      <Card className="m20" size="small">
        <Table
          rowKey="Id"
          size="small"
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          columns={[
            { title: '固件名称', dataIndex: 'Name', width: 140 },
            { title: '版本', dataIndex: 'Version', width: 100 },
            { title: '文件', dataIndex: 'FileName', width: 160, ellipsis: true },
            { title: '大小', dataIndex: 'FileSize', width: 90, render: (v) => formatSize(v) },
            { title: '分片数', dataIndex: 'ChunkCount', width: 80 },
            {
              title: '适用产品/型号',
              key: 'target',
              width: 160,
              render: (_, r) => r.ProductId || r.DeviceModel || '-',
            },
            {
              title: '上传时间',
              dataIndex: 'Created',
              width: 150,
              render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
            {
              title: '操作',
              key: 'action',
              width: 80,
              render: (_, r) => (
                <Popconfirm title="确定删除此固件？" onConfirm={() => handleDelete(r.Id)}>
                  <Typography.Link type="danger">删除</Typography.Link>
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>
      <FirmwareModal open={uploadOpen} onClose={() => setUploadOpen(false)} onSuccess={reload} />
    </>
  );
};

const JobTab: React.FC<{ onDetail: (job: OtaJob) => void }> = ({ onDetail }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const { loading, dataSource, pagination, form, reload, search, reset } = useTableRequest<OtaJob>({
    onRequest: (params) => getOtaJobs(params),
  });

  // 固件 Id -> 版本 映射，用于展示“固件版本”。
  const { data: firmwareData } = useRequest(getFirmwares, { defaultParams: [{ page: 1, pageSize: 1000 }] });
  const versionMap: Record<string, string> = {};
  (firmwareData?.result?.list || []).forEach((f) => {
    versionMap[f.Id] = f.Version;
  });

  const act = async (fn: (id: string) => Promise<any>, id: string, ok: string) => {
    const resp = await fn(id);
    if (resp.success) {
      messageApi.success(ok);
      reload();
    } else {
      messageApi.error(resp.errorMsg || '操作失败');
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="inline" className="ml20 mt20" form={form}>
        <Form.Item name="status">
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 140 }}
            options={[
              { value: '', label: '全部' },
              ...Object.entries(jobStatusMap).map(([value, m]) => ({ value, label: m.label })),
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={search}>查询</Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={reset}>重置</Button>
        </Form.Item>
      </Form>
      <Card className="m20" size="small">
        <Table
          rowKey="Id"
          size="small"
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          columns={[
            { title: '任务ID', dataIndex: 'Id', width: 180, ellipsis: true },
            { title: '固件版本', dataIndex: 'FirmwareId', width: 100, render: (v) => versionMap[v] || v || '-' },
            { title: '设备总数', dataIndex: 'TotalDevices', width: 80 },
            { title: '成功数', dataIndex: 'SuccessCount', width: 70 },
            { title: '失败数', dataIndex: 'FailedCount', width: 70 },
            {
              title: '状态',
              dataIndex: 'Status',
              width: 90,
              render: (v) => {
                const m = jobStatusMap[v];
                return m ? (
                  <>
                    <Badge color={m.color} />
                    <Text className="ml5">{m.label}</Text>
                  </>
                ) : (
                  v || '-'
                );
              },
            },
            {
              title: '进度',
              key: 'progress',
              width: 140,
              render: (_, r) => {
                const done = r.SuccessCount + r.FailedCount;
                const pct = r.TotalDevices ? Math.floor((done * 100) / r.TotalDevices) : 0;
                return <Progress percent={pct} size="small" />;
              },
            },
            {
              title: '创建时间',
              dataIndex: 'Created',
              width: 150,
              render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
            {
              title: '操作',
              key: 'action',
              width: 200,
              render: (_, r) => (
                <Space split={<Text type="secondary">/</Text>}>
                  <Typography.Link onClick={() => onDetail(r)}>详情</Typography.Link>
                  {r.Status === 'pending' && (
                    <Typography.Link onClick={() => act(startOtaJob, r.Id, '已启动')}>启动</Typography.Link>
                  )}
                  {['pending', 'offered', 'transferring', 'verifying'].includes(r.Status) && (
                    <Typography.Link onClick={() => act(cancelOtaJob, r.Id, '已取消')}>取消</Typography.Link>
                  )}
                  {r.Status === 'failed' && (
                    <Typography.Link onClick={() => act(retryOtaJob, r.Id, '已重试')}>重试</Typography.Link>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
};

const OtaPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailJob, setDetailJob] = useState<OtaJob | null>(null);
  const presetDeviceIds = searchParams.get('deviceId') ? [searchParams.get('deviceId') as string] : undefined;
  const autoCreate = searchParams.get('create') === '1';

  const items = [
    { key: 'firmware', label: '固件管理', children: <FirmwareTab /> },
    {
      key: 'job',
      label: '升级任务',
      children: (
        <>
          <JobTab onDetail={(j) => setDetailJob(j)} />
          <div className="m20">
            <Button type="primary" onClick={() => setCreateOpen(true)}>创建升级任务</Button>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <Tabs
        className="m20"
        items={items}
        activeKey={autoCreate ? 'job' : undefined}
        onChange={(key) => {
          // 消费一次 query 参数，避免刷新后重复打开
          if (autoCreate || presetDeviceIds) setSearchParams({}, { replace: true });
        }}
      />
      <CreateJobDrawer
        open={createOpen || autoCreate}
        presetDeviceIds={presetDeviceIds}
        onClose={() => { setCreateOpen(false); if (autoCreate) setSearchParams({}, { replace: true }); }}
        onSuccess={() => {}}
      />
      {detailJob && (
        <JobDetailDrawer
          open={!!detailJob}
          jobId={detailJob.Id}
          onClose={() => setDetailJob(null)}
        />
      )}
    </>
  );
};

export default OtaPage;
